import lodash from 'lodash';

// Helpers for normalising rich-text (TinyMCE) chapter content before it is saved,
// previewed or loaded back into the editor. Kept framework-free so it can be shared
// between the editor component and the authoring-tool modal.

// Characters that look empty to the user but survive String.trim()
// (nbsp, zero-width space/non-joiner/joiner, BOM).
const INVISIBLE_CHARS = /[\u00a0\u200b\u200c\u200d\ufeff]/g;

// Parses a rich-text fragment into the body of an inert document, so no image, media or other
// resource of the content is loaded. An inert document is used instead of DOMParser because the
// browser of the test runner does not parse 'text/html' documents.
function parseHtml (html) {
    const inertDocument = document.implementation.createHTMLDocument('');
    inertDocument.body.innerHTML = html;

    return inertDocument.body;
}

// True when a node carries no visible content of its own: whitespace/invisible text,
// a <br>, or an empty <p>/<div>. Media (and anything wrapping it) is never "empty".
function isTrailingEmpty (node) {
    if (!node) return false;
    if (node.nodeType === 3) return node.textContent.replace(INVISIBLE_CHARS, '').trim() === '';
    if (node.nodeType !== 1) return false;
    if (node.tagName === 'BR') return true;
    if (['P', 'DIV'].indexOf(node.tagName) === -1) return false;
    if (node.querySelector('img, iframe, video, embed, figure, table, hr')) return false;

    return node.textContent.replace(INVISIBLE_CHARS, '').trim() === '';
}

// Removes empty lines (blank paragraphs/divs, <br>, whitespace) from the end of the
// content so saving/previewing doesn't keep trailing blank lines. Non-empty content
// and media are kept.
export function stripTrailingEmptyParagraphs (html) {
    if (!html) return html;

    const body = parseHtml(html);

    while (body.lastChild && isTrailingEmpty(body.lastChild)) {
        body.removeChild(body.lastChild);
    }

    return body.innerHTML;
}

// Elements that never belong to the read-only comparison: they would run code, change the styles
// of the page or load resources.
const REMOVED_TAGS = ['SCRIPT', 'STYLE', 'LINK', 'META', 'BASE', 'NOSCRIPT', 'TEMPLATE', 'FORM', 'INPUT', 'BUTTON'];

// Elements whose media is shown in the comparison as a video placeholder.
const VIDEO_TAGS = ['VIDEO', 'IFRAME', 'EMBED', 'OBJECT'];

// Extensions that are offered to the learner as a file instead of being played or shown.
const FILE_EXTENSIONS = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt', 'zip', 'rtf', 'odt'];

function cleanText (text) {
    return text.replace(INVISIBLE_CHARS, ' ').replace(/\s+/g, ' ').trim();
}

// Last part of a URL without the query string, used when the media carries no other name.
function fileNameFromUrl (url) {
    if (!url) return null;

    const path = String(url).split(/[?#]/)[0];
    const name = path.split('/').pop();

    return name ? decodeURIComponent(name) : null;
}

function fileExtension (url) {
    const name = fileNameFromUrl(url);
    if (!name || name.indexOf('.') === -1) return null;

    return name.split('.').pop().toLowerCase();
}

function mediaSource (element) {
    const source = element.getAttribute('src');
    if (source) return source;

    const child = element.querySelector('source[src]');

    return child ? child.getAttribute('src') : null;
}

// Describes a media element with the metadata that is actually present in the content, so the
// comparison can show a placeholder instead of loading the media itself. Anything that is not
// media returns null.
function describeMedia (element) {
    const tag = element.tagName;

    if (tag === 'IMG') {
        const source = element.getAttribute('src');

        return {
            type: 'image',
            name: element.getAttribute('alt') || element.getAttribute('title') || fileNameFromUrl(source),
            format: fileExtension(source)
        };
    }

    if (lodash.includes(VIDEO_TAGS, tag)) {
        const source = tag === 'OBJECT' ? element.getAttribute('data') : mediaSource(element);

        return {
            type: 'video',
            name: element.getAttribute('title') || fileNameFromUrl(source),
            format: fileExtension(source)
        };
    }

    if (tag === 'AUDIO') {
        const source = mediaSource(element);

        return {
            type: 'audio',
            name: element.getAttribute('title') || fileNameFromUrl(source),
            format: fileExtension(source)
        };
    }

    if (tag === 'A') {
        const href = element.getAttribute('href');
        const extension = fileExtension(href);
        if (!extension || FILE_EXTENSIONS.indexOf(extension) === -1) return null;

        return {
            type: 'file',
            name: cleanText(element.textContent) || fileNameFromUrl(href),
            format: extension
        };
    }

    return null;
}

// Removes what could run code or load a resource from an element that stays in the comparison.
function cleanElement (element) {
    Array.prototype.slice.call(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = String(attribute.value).replace(/\s+/g, '').toLowerCase();

        if (name.indexOf('on') === 0 || name === 'srcset' || /^(javascript|vbscript|data):/.test(value)) {
            element.removeAttribute(attribute.name);
        }
    });

    if (element.style && /url\(/i.test(element.getAttribute('style') || '')) {
        element.style.removeProperty('background');
        element.style.removeProperty('background-image');
    }
}

// Prepares rich-text chapter content for the read-only source comparison. The content stays as it
// is (text formatting, tables, lists, colours), only the media is replaced in its position by the
// placeholder the caller builds from the metadata that actually exists in the content, so no image,
// video or file is loaded.
//
// @param html chapter content
// @param createPlaceholder (media, ownerDocument) => Node, media = {type, name, format}
export function buildComparisonHtml (html, createPlaceholder) {
    if (!html) return '';

    const body = parseHtml(html);

    const walk = (node) => {
        Array.prototype.slice.call(node.childNodes).forEach((child) => {
            if (child.nodeType === 8) {
                node.removeChild(child);
                return;
            }
            if (child.nodeType !== 1) return;

            if (REMOVED_TAGS.indexOf(child.tagName) !== -1) {
                node.removeChild(child);
                return;
            }

            const media = describeMedia(child);
            if (media) {
                node.replaceChild(createPlaceholder(media, body.ownerDocument), child);
                return;
            }

            cleanElement(child);
            walk(child);
        });
    };

    walk(body);

    return body.innerHTML;
}



// WEBPACK FOOTER //
// ./src/app/services/htmlContent/index.js